import {DataGranularity, HistoryMetric} from '../../../api/types';
import * as Units from '../../Units';
import {DateTimeConfig} from '../../Dates';
import {
  createXAxisConfig,
  createYAxisConfig,
  createTooltipConfig,
  createGridConfig,
  createLegendConfig,
} from '../builders';
import {getSeriesColor, ChartTheme} from '../theme';
import {ChartConfig, ChartDataPoint, SeriesConfig} from '../types';

export interface TemperatureFeelChartOptions {
  data: ChartDataPoint[];
  theme: ChartTheme;
  timezone: string;
  units: Units.Units;
  granularity: DataGranularity;
  /** Metrics to display (e.g. ['T', 'HEAT_INDEX', 'WIND_CHILL', 'HUMIDEX']) */
  metrics: HistoryMetric[];
  /** Display names for each metric */
  metricNames: Record<string, string>;
  height?: number;
  dateTimeConfig?: DateTimeConfig;
}

const FEEL_COLORS = ['primary', 'error', 'info', 'warning', 'success'] as const;

export const temperatureFeelChart = (
  options: TemperatureFeelChartOptions,
): ChartConfig => {
  const {
    data,
    theme,
    timezone,
    units,
    granularity,
    metrics,
    metricNames,
    height = 400,
    dateTimeConfig,
  } = options;

  const colorKeys: Record<string, string> = {
    primary: theme.colors.primary,
    error: theme.colors.error,
    info: theme.colors.info,
    warning: theme.colors.warning,
    success: theme.colors.success,
  };

  const series: SeriesConfig[] = metrics.map((metric, index) => {
    const colorKey = FEEL_COLORS[index % FEEL_COLORS.length];
    const color = colorKeys[colorKey] ?? getSeriesColor(index, theme.isDark);

    return {
      id: `feel-${metric}`,
      type: 'line' as const,
      dataKey: metric,
      name: metricNames[metric] ?? metric,
      color,
      yAxisIndex: 0,
      interpolation: 'monotone' as const,
      lineWidth: 2,
      showMarkers: false,
      zIndex: index === 0 ? 2 : 1,
    };
  });

  return {
    height,
    data,
    series,
    xAxis: createXAxisConfig({type: 'datetime', timezone, granularity, theme}),
    yAxes: [
      createYAxisConfig({
        id: 'temperature',
        metric: 'T',
        units,
        theme,
      }),
    ],
    tooltip: createTooltipConfig({
      timezone,
      granularity,
      metric: 'T',
      units,
      theme,
      dateTimeConfig,
    }),
    legend: createLegendConfig(true, theme),
    grid: createGridConfig(theme),
    animate: true,
  };
};
